import { ref, onMounted, computed, watch } from '@vue/composition-api';
import { api } from '@/api/api';
import { createDataset } from './services/createDataset';

const PRIMARY_PARAMETERS = ['marketer', 'channel', 'source'];
const allFilled = (parameters) =>
  parameters.every(({ values }) => values.length === 1);

const getInitialSelection = (obj) => {
  const primary = [];
  const secondary = [];
  Object.entries(obj).forEach(([name, value]) => {
    const selection = { name, value };
    if (PRIMARY_PARAMETERS.includes(name)) {
      primary.push(selection);
    } else {
      secondary.push(selection);
    }
  });
  return { primary, secondary };
};

export const useMarketerParameters = (template) => {
  const dataset = createDataset();
  const datasetSecondary = createDataset();

  const parameters = ref([]);
  const fetching = ref(false);
  const secondaryVisible = ref(false);
  const parametersSecondary = ref([]);
  const fetchingSecondary = ref(false);

  const primaryFilled = computed(() => allFilled(parameters.value));
  const completed = computed(
    () => primaryFilled.value && allFilled(parametersSecondary.value)
  );

  const fetchParametersSecondary = async (parameters) => {
    if (parameters.length === 0) {
      return;
    }
    fetchingSecondary.value = true;
    const query = parameters
      .map(
        ({ name, values }) =>
          `${encodeURIComponent(name)}=${encodeURIComponent(values[0])}`
      )
      .join('&');
    const { data } = await api.get(`marketer_mapping/?${query}`);
    fetchingSecondary.value = false;
    parametersSecondary.value = datasetSecondary.set(data);
  };
  const toggleSecondary = () => {
    if (!primaryFilled.value) {
      secondaryVisible.value = false;
      datasetSecondary.clear();
      return;
    }
    secondaryVisible.value = true;
    fetchParametersSecondary(parameters.value);
  };

  watch(template, (templateValue) => {
    if (!templateValue || !templateValue.marketer_data_selection) {
      return;
    }
    const { primary, secondary } = getInitialSelection(
      templateValue.marketer_data_selection
    );
    parameters.value = dataset.updateSelections(primary);
    datasetSecondary.updateSelections(secondary);
    toggleSecondary();
  });

  const fetchParameters = async () => {
    fetching.value = true;
    const { data } = await api.get(
      `partial_marketer_mapping/?${PRIMARY_PARAMETERS.map(
        (p) => `include_${p}=true`
      ).join('&')}`
    );
    fetching.value = false;
    parameters.value = dataset.set(data);
    toggleSecondary();
  };

  const selectParameter = (parameter) => {
    parameters.value = dataset.select(parameter);
    toggleSecondary();
  };
  const resetParameter = (parameter) => {
    parameters.value = dataset.reset(parameter);
    toggleSecondary();
  };
  const selectParameterSecondary = (parameter) => {
    parametersSecondary.value = datasetSecondary.select(parameter);
  };
  const resetParameterSecondary = (parameter) => {
    parametersSecondary.value = datasetSecondary.reset(parameter);
  };

  onMounted(fetchParameters);

  return {
    selectParameter,
    resetParameter,
    fetchingMarketerParameters: fetching,
    marketerSelections() {
      const selections = [
        ...dataset.getSelections(),
        ...datasetSecondary.getSelections(),
      ].map(({ name, value }) => [name, value]);
      return Object.fromEntries(selections);
    },
    marketerParameters: parameters,
    selectParameterSecondary,
    resetParameterSecondary,
    marketerParametersSecondaryVisible: secondaryVisible,
    fetchingMarketerParametersSecondary: fetchingSecondary,
    marketerParametersSecondary: parametersSecondary,
    marketerParametersCompleted: completed,
  };
};
